import { Material } from "@/models/material.model";
import { Pagination, QueryParams } from "@/models/query-params.model";
import { MaterialsRest } from "@/services/rest/materials.rest";
import { ObjectivesRest } from "@/services/rest/objectives.rest";
import { QuizRest } from "@/services/rest/quiz.rest";
import { GenericsUtils } from "@/utils/generics.util";
import { BehaviorSubject, first, map, take } from "rxjs";

export class ObjectiveService {
  constructor(
    private _objectives = new ObjectivesRest(),
    private _materials = new MaterialsRest(),
    private _quiz = new QuizRest()
  ) {}

  private objective$ = new BehaviorSubject<any>([]);
  objective = this.objective$.asObservable();
  private objectives$ = new BehaviorSubject<any>([]);
  objectives = this.objectives$.asObservable();
  private materials$ = new BehaviorSubject({} as Pagination<Material>);
  materials = this.materials$.asObservable();
  private quiz$ = new BehaviorSubject<any>([]);
  quiz = this.quiz$.asObservable();
  private questionsDefault$ = new BehaviorSubject<any>([]);
  questionsDefault = this.questionsDefault$.asObservable();
  private questions$ = new BehaviorSubject<any>([]);
  questions = this.questions$.asObservable();
 

  getObjectiveById(chapterId: string, trailId: string): void {
    this._objectives
      .getObjectiveById(chapterId, trailId)
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          this.objective$.next(response);
        },
      });
  }

  getObjectives(chapterId: string, params?: QueryParams): void {
    this._objectives
      .getObjectives(chapterId, params)
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          this.objectives$.next(response);
        },
      });
  }

  getMaterialsFromObjective(objectiveId: string, params: QueryParams): void {
    this._materials
      .getObjectsByLearningObjective(objectiveId, params)
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          this.materials$.next(response);
        },
      });
  }

  getMaterialsByObjective(
    objectiveId: string,
    params: QueryParams
  ): Promise<Pagination<Material>> {
    return this._materials
      .getObjectsByLearningObjective(objectiveId, params)
      .pipe(take(1))
      .toPromise()
      .then((response) => response!);
  }

  getQuizByTeacher(objectiveId: string): void {
    this._quiz
      .getQuizByTeacher(objectiveId)
      .pipe(
        take(1),
        map((e) => {
          const totalMs = e.duration * 1000;
          const result = new Date(totalMs).toISOString().slice(11, 19);
          e.duration = result;
          return e;
        })
      )
      .subscribe({
        next: (response) => {
          this.quiz$.next(response);
        },
        error: (error) => {
          this.quiz$.next(null);
        },
      });
  }

  getQuestionsDefaultByQuiz(quizId: string, params: QueryParams): void {
    this._quiz
      .getQuestionsDefaultByQuiz(quizId, params)
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          this.questionsDefault$.next(response);
        },
      });
  }

  getQuestionsByQuiz(quizId: string, params: QueryParams): void {
    this._quiz
      .getQuestionsByQuiz(quizId, params)
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          const currentQuestions = this.questions$.getValue() || { content: [] };
          currentQuestions.content = Array.isArray(currentQuestions.content) ? currentQuestions.content : [];

          const responseContent = Array.isArray(response.content) ? response.content : [];
          const questions = [...currentQuestions.content, ...responseContent];
          const updatedResponse = { ...response, content: questions };
          this.questions$.next(updatedResponse);
        },
      });
  }
  
  sortByLearningGroupType(materials: Material[]): Material[] {
    const priorities = {
      MOTIVATION: 0,
      INTRODUCTION: 1,
      DEVELOPMENT: 2,
      CLOSURE: 3,
    };
    materials.sort((a, b) => {
      const priorityA = priorities[a.learningGroupType];
      const priorityB = priorities[b.learningGroupType];
      return priorityA - priorityB;
    });
    return materials;
  }
  updateObjective(chapterId: string, objectiveId: string, data: any) {
    this._objectives
      .updateObjective(chapterId, objectiveId, data)
      .subscribe({ next: () => {}, error: (e) => {} });
  }
}
